import React from 'react';

/**
 * Opt-in seam for an overlay that renders proposed answers inside the ordinary
 * form controls.
 *
 * The engine stays generic: with no adapter installed — every form today —
 * `DraftAwareFieldControl` renders exactly what `FieldControl` rendered before,
 * with react-hook-form's own value and onChange. An adapter may substitute the
 * value a control shows, redirect its edits, and wrap it in review chrome.
 *
 * The seam is a value/onChange swap rather than a parallel renderer on purpose:
 * conditional visibility, validation and every field type keep working because
 * the same `FieldControl` is still doing the rendering.
 */
export interface FieldDecoration {
  /** What the control should display. */
  readonly value: unknown;
  /** Where its edits go. */
  readonly onChange: (value: unknown) => void;
  /** Review chrome around the control. Identity when there is nothing to show. */
  readonly wrap?: (control: React.ReactNode) => React.ReactNode;
}

export type FieldDecorator = (args: {
  readonly fieldId: string;
  readonly value: unknown;
  readonly onChange: (value: unknown) => void;
}) => FieldDecoration;

const FieldDraftContext = React.createContext<FieldDecorator | null>(null);

export const FieldDraftProvider = FieldDraftContext.Provider;

export const useFieldDecorator = (): FieldDecorator | null =>
  React.useContext(FieldDraftContext);
